// @flow

import _ from 'lodash'
import type { DisplayProduct } from 'core/service/display-product'
import type { Rider, RiderState } from 'core/data-model/rider'
import type { BasicPremium$, SumAssured$, TotalPremium$ } from 'core/data-model/basic-plan'

import CONSTANTS from 'core/data-model/constants/values'

import { isNil, isNotNil } from 'core/service/lib/type-check'
import { createSelector } from 'reselect'
import { getSelectedDisplayProduct, getDisplayProducts } from 'quick-quote/product-selection/selectors'
import { canProceedToCoveragePlan } from 'quick-quote/selectors/next-tab-proceeding'

import { getAge } from 'quick-quote/insured-information/selectors'

import {
  getSumAssured$,
  getSelectedRiders,
  getBasicPremium$,
  getTotalPremium$,
  getBasicPlanErrorMessage,
  getPensionType,
} from 'quick-quote/product-common/coverage-plan/selectors'

export const canProceedBumnanReadyBenefitIllustration = createSelector(
  getSumAssured$,
  getBasicPremium$,
  getTotalPremium$,
  getBasicPlanErrorMessage,
  getSelectedRiders,
  canProceedToCoveragePlan,
  getSelectedDisplayProduct,
  getPensionType,
  (
    sumAssured$: SumAssured$,
    basicPremium: BasicPremium$,
    totalPremium$: TotalPremium$,
    basicPlanErrorMessage: string,
    selectedRiders: (Rider & RiderState)[],
    canProceedToCoveragePlan: boolean,
    selectedDisplayProduct: DisplayProduct,
    pensionType: string
  ): boolean => {
    const selectedRiderInvalidMessages = selectedRiders.filter((rider) =>
      isNotNil(rider.errors.find((error) => error.type === CONSTANTS.VALIDATION))
    )

    const canProceed =
      canProceedToCoveragePlan &&
      sumAssured$.isOk &&
      basicPremium.isOk &&
      totalPremium$.isOk &&
      !_.isEmpty(pensionType) &&
      _.isEmpty(selectedRiderInvalidMessages) &&
      _.isEmpty(basicPlanErrorMessage)

    if (selectedDisplayProduct.type === CONSTANTS.CAMPAIGN_PRODUCT_TYPE) {
      const selectedRiderCodes = selectedRiders.map((rider) => rider.code)
      return (
        canProceed &&
        selectedDisplayProduct.requiredRiders != null &&
        selectedDisplayProduct.requiredRiders.every((riderCode) => {
          return selectedRiderCodes.indexOf(riderCode) > -1
        })
      )
    }

    return canProceed
  }
)

export const getValidSelectedRiders = createSelector(getSelectedRiders, (selectedRiders) => {
  return selectedRiders.filter((rider) =>
    isNil(rider.errors.find((error) => error.type !== CONSTANTS.ADDITIONAL_INFORMATION))
  )
})

const _isValidAge = (entryAge, age) => {
  const { minimum, maximum } = entryAge
  const insureAge: number = age.unit !== 'year' ? 0 : age.value
  return insureAge >= minimum.value && insureAge <= maximum.value
}

export const getSelectableProductBumnanReady = createSelector(getAge, getDisplayProducts, (age: Age, products) => {
  const productGroup = _.filter(products, (product) =>
    _.includes(CONSTANTS.BUMNAN_READY_CODE_GROUP, _.get(product, 'code'))
  )
  const validProducts = _.filter(productGroup, (product) => _isValidAge(_.get(product, 'entryAge'), age))
  const validProductCodes = validProducts.map((product) => product.code)
  return validProductCodes
})

export const getSelectedProductBumnanReady = (selectableProductBumnanReady, selectedProduct) => {
  // if current is not valid return the first valie
  return selectableProductBumnanReady.includes(selectedProduct) ? selectedProduct : selectableProductBumnanReady[0]
}
